import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { formatAppMoney } from '../../services/appConfigRuntime';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Pagination,
  Stack,
  Collapse,
  Tooltip,
  Chip,
  Grid
} from '@mui/material';
import {
  ArrowBack, 
  ExpandMore,
  CheckCircle, 
  Warning, 
  Info, 
  SyncAlt,
  Search,
  FilterList,
  Refresh,
  Close,
  FileDownload,
  Link
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + 'synctrack';

const BudgetToItemDatapoint = () => {
  const { flowInstanceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [itemData, setItemData] = useState(null);
  const [syncRecords, setSyncRecords] = useState([]);
  const [error, setError] = useState(null);
  
  // UI State Management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  
  // Get data from navigation state
  const { masterFlowId, flowName, tenantId } = location.state || {};

  // Track previous connectors to detect changes
  const previousConnectorsRef = useRef(null);

  // Clear data state helper
  const clearAllDataState = () => {
    setBudgetData(null);
    setItemData(null);
    setSyncRecords([]);
  };

  // Clear data for specific connectors
  const clearDataForConnectors = (connectorNames) => {
    if (connectorNames.includes('Autodesk')) {
      setBudgetData(null);
    }
    if (connectorNames.includes('QuickBooks')) {
      setItemData(null);
    }
  };

  useEffect(() => {
    if (flowInstanceId && tenantId) {
      fetchDataPointConfig();
    } else {
      setError('Missing required parameters');
      setLoading(false);
    }
  }, [flowInstanceId, tenantId]);

  // Detect connector changes and clear relevant data
  useEffect(() => {
    if (!config?.connectors) return;

    const currentConnectors = JSON.stringify(config.connectors.map(c => ({ mcId: c.mcId, mcName: c.mcName })));
    const previousConnectors = previousConnectorsRef.current;

    if (previousConnectors && previousConnectors !== currentConnectors) {
      console.log('🔄 Connector configuration changed, clearing relevant data...');
      
      const previous = JSON.parse(previousConnectors);
      const current = config.connectors.map(c => ({ mcId: c.mcId, mcName: c.mcName }));
      
      const changedConnectors = [];
      previous.forEach(prev => {
        const currentConn = current.find(c => c.mcId === prev.mcId);
        if (!currentConn || currentConn.mcName !== prev.mcName) {
          changedConnectors.push(prev.mcName);
        }
      });
      current.forEach(curr => {
        const prevConn = previous.find(p => p.mcId === curr.mcId);
        if (!prevConn || prevConn.mcName !== curr.mcName) {
          changedConnectors.push(curr.mcName);
        }
      });

      if (changedConnectors.length > 0) {
        clearDataForConnectors(changedConnectors);
        fetchDataPointConfig();
      }
    }

    previousConnectorsRef.current = currentConnectors;
  }, [config?.connectors]);

  const fetchDataPointConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      // Step 1: Get data point configuration (connector IDs)
      const configResponse = await fetch(`${API_BASE_URL}/datapoint/flow/${flowInstanceId}/tenant/${tenantId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!configResponse.ok) {
        throw new Error(`Failed to fetch config: ${configResponse.status}`);
      }

      const configData = await configResponse.json();
      console.log('BudgetToItemDatapoint: Data Point config:', configData);
      setConfig(configData);
      
      // Step 2: Fetch sync records
      let records = [];
      const flowIdForConnector = configData.flowInstanceId || configData.masterFlowId;
      if (configData.masterFlowId) {
        records = await fetchSyncRecords(configData.masterFlowId, token);
      }
      
      setSyncRecords(records);

      // Step 3: Fetch Autodesk budgets (directly using connector from config)
      // Prefer connector type (stable); fall back to mcName when mc_connector_type is null (e.g. seed data)
      const autodeskConnector = configData.connectors?.find(c =>
        (c.mcConnectorType && c.mcConnectorType.toUpperCase() === 'AUTODESK') ||
        (c.mcName && (c.mcName === 'Autodesk' || c.mcName.includes('Autodesk')))
      );
      
      if (autodeskConnector && autodeskConnector.mcId) {
        console.log('BudgetToItemDatapoint: Fetching Autodesk budgets...');
        const budgetUrl = `${API_BASE_URL}/autodesk/budgets/by-connector?mcId=${autodeskConnector.mcId}&flowId=${flowIdForConnector}&tenantId=${tenantId}`;
        
        try {
          const budgetResponse = await fetch(budgetUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (budgetResponse.ok) {
            const budgetResult = await budgetResponse.json();
            console.log('BudgetToItemDatapoint: Budget data received:', budgetResult);
            setBudgetData(budgetResult);
          } else {
            const errorText = await budgetResponse.text();
            console.error('BudgetToItemDatapoint: Failed to fetch budgets:', budgetResponse.status, errorText);
            toast.warning('Failed to fetch Autodesk budgets. Please check connector configuration.');
          }
        } catch (error) {
          console.error('BudgetToItemDatapoint: Error fetching budgets:', error);
          toast.warning('Error fetching Autodesk budgets: ' + error.message);
        }
      } else {
        console.warn('BudgetToItemDatapoint: No Autodesk connector found');
      }
      
      // Step 4: Fetch QuickBooks items (directly using connector from config)
      // Prefer connector type (stable); fall back to mcName when mc_connector_type is null (e.g. seed data)
      const quickbooksConnector = configData.connectors?.find(c =>
        (c.mcConnectorType && c.mcConnectorType.toUpperCase() === 'QUICKBOOKS') ||
        (c.mcName && (c.mcName === 'QuickBooks' || c.mcName.includes('QuickBooks')))
      );
      
      if (quickbooksConnector && quickbooksConnector.mcId) {
        console.log('BudgetToItemDatapoint: Fetching QuickBooks items...');
        const itemUrl = `${API_BASE_URL}/quickbooks/items/by-connector?mcId=${quickbooksConnector.mcId}&flowId=${flowIdForConnector}&tenantId=${tenantId}`;
        
        try {
          const itemResponse = await fetch(itemUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (itemResponse.ok) {
            const itemResult = await itemResponse.json();
            console.log('BudgetToItemDatapoint: Item data received:', itemResult);
            setItemData(itemResult);
          } else {
            const errorText = await itemResponse.text();
            console.error('BudgetToItemDatapoint: Failed to fetch items:', itemResponse.status, errorText);
            toast.warning('Failed to fetch QuickBooks items. Please check connector configuration.');
          }
        } catch (error) {
          console.error('BudgetToItemDatapoint: Error fetching items:', error);
          toast.warning('Error fetching QuickBooks items: ' + error.message);
        }
      } else {
        console.warn('BudgetToItemDatapoint: No QuickBooks connector found');
      }
      
    } catch (error) {
      console.error('BudgetToItemDatapoint: Error fetching data point:', error);
      setError(`Failed to fetch data: ${error.message}`);
      toast.error(`Failed to fetch data point: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncRecords = async (masterFlowId, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/datapoint/sync-records/${masterFlowId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sync records: ${response.status}`);
      }

      const result = await response.json();
      return result.records || [];
    } catch (error) {
      console.error('BudgetToItemDatapoint: Error fetching sync records:', error);
      return [];
    }
  };

  // Merge budget and item data
  const mergedData = useMemo(() => {
    const merged = [];
    
    const budgets = budgetData?.results || [];
    const items = itemData?.Item || [];
    
    console.log('BudgetToItemDatapoint: Merging data...');
    console.log('  - Budgets count:', budgets.length);
    console.log('  - Items count:', items.length);
    
    // Create a map of sync records by source ID (budget code/name)
    const syncRecordMap = new Map();
    syncRecords.forEach(record => {
      const sourceKey = record.sourceDesc || record.sourceId;
      if (sourceKey) {
        syncRecordMap.set(sourceKey, record);
      }
    });
    
    // Match budgets with items
    const matchedItemIds = new Set();
    
    budgets.forEach(budget => {
      const budgetCode = budget.code || budget.name || '';
      const budgetName = budget.name || budgetCode;
      const matchKey = budgetCode || budgetName;
      
      // Try to find matching item by name or SKU
      const matchedItem = items.find(item => {
        const itemName = item.Name || '';
        const itemSku = item.Sku || '';
        return itemName === budgetName || itemName === budgetCode || 
               itemSku === budgetCode || itemSku === budgetName;
      });
      
      const syncRecord = syncRecordMap.get(matchKey);
      
      if (matchedItem) {
        matchedItemIds.add(matchedItem.Id);
        merged.push({
          type: 'budget-item',
          status: syncRecord?.status === 'success' || syncRecord?.status === 'Synced' ? 'matched' : 'matched',
          source: budget,
          target: matchedItem,
          matchKey: matchKey,
          syncRecord: syncRecord
        });
      } else {
        merged.push({
          type: 'budget-item',
          status: 'source-only',
          source: budget,
          target: null,
          matchKey: matchKey,
          syncRecord: syncRecord
        });
      }
    });
    
    // Find items not matched to any budget
    items.forEach(item => {
      if (!matchedItemIds.has(item.Id)) {
        merged.push({
          type: 'budget-item',
          status: 'target-only',
          source: null,
          target: item,
          matchKey: item.Name || item.Sku || item.Id
        });
      }
    });
    
    console.log('BudgetToItemDatapoint: Merged data count:', merged.length);
    return merged;
  }, [budgetData, itemData, syncRecords]);

  // Filter and search functionality
  const filteredData = useMemo(() => {
    let filtered = mergedData;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const sourceSearch = item.source ? 
          `${item.source.code || ''} ${item.source.name || ''}`.toLowerCase() : '';
        const incomeAccountRef = item.target?.IncomeAccountRef 
          ? (typeof item.target.IncomeAccountRef === 'string' 
              ? item.target.IncomeAccountRef 
              : (item.target.IncomeAccountRef.value || item.target.IncomeAccountRef.name || item.target.IncomeAccountRef.FullName || ''))
          : '';
        const expenseAccountRef = item.target?.ExpenseAccountRef 
          ? (typeof item.target.ExpenseAccountRef === 'string' 
              ? item.target.ExpenseAccountRef 
              : (item.target.ExpenseAccountRef.value || item.target.ExpenseAccountRef.name || item.target.ExpenseAccountRef.FullName || ''))
          : '';
        const targetSearch = item.target ? 
          `${item.target.Name || ''} ${item.target.Sku || ''} ${item.target.Description || ''} ${incomeAccountRef} ${expenseAccountRef}`.toLowerCase() : '';
        return sourceSearch.includes(searchLower) || targetSearch.includes(searchLower);
      });
    }

    return filtered;
  }, [mergedData, statusFilter, searchTerm]);

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Reset to page 1 when search term or status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handleBack = () => {
    navigate('/sync-flow');
  };

  const handleRefresh = () => {
    setLoading(true);
    clearAllDataState();
    fetchDataPointConfig();
  };

  const getRowStyle = (status) => {
    switch (status) {
      case 'matched':
        return { backgroundColor: '#e8f5e9' }; // Light green
      case 'source-only':
        return { backgroundColor: '#fff3e0' }; // Light orange
      case 'target-only':
        return { backgroundColor: '#e3f2fd' }; // Light blue
      default:
        return {};
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'matched':
        return <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />;
      case 'source-only':
        return <Warning sx={{ color: '#ff9800', fontSize: 20 }} />;
      case 'target-only':
        return <Info sx={{ color: '#2196f3', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'matched':
        return 'Synced';
      case 'source-only':
        return 'Not in Target';
      case 'target-only':
        return 'Target Only';
      default:
        return 'Unknown';
    }
  };

  const formatAmount = (amount) => {
    if (amount == null || amount === undefined) return 'N/A';
    try {
      const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
      if (isNaN(numAmount)) return 'N/A';
      return formatAppMoney(numAmount);
    } catch (e) {
      return 'N/A';
    }
  };

  const escapeCsvCell = (val) => {
    const s = String(val == null ? '' : val);
    if (/[,"\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };

  const handleExportCsv = () => {
    if (!filteredData.length) {
      toast.info('No data to export');
      return;
    }
    const headers = [
      'Sync Status',
      'Budget Code',
      'Budget Name',
      'Budget Amount',
      'Item Name',
      'Item SKU',
      'Income Account Ref',
      'Expense Account Ref'
    ];
    const getRowValues = (item) => {
      const incomeRef = item.target?.IncomeAccountRef
        ? (typeof item.target.IncomeAccountRef === 'string'
            ? item.target.IncomeAccountRef
            : (item.target.IncomeAccountRef.value || item.target.IncomeAccountRef.name || item.target.IncomeAccountRef.FullName || ''))
        : '';
      const expenseRef = item.target?.ExpenseAccountRef
        ? (typeof item.target.ExpenseAccountRef === 'string'
            ? item.target.ExpenseAccountRef
            : (item.target.ExpenseAccountRef.value || item.target.ExpenseAccountRef.name || item.target.ExpenseAccountRef.FullName || ''))
        : '';
      return [
        getStatusLabel(item.status),
        item.source?.code ?? '',
        item.source?.name ?? '',
        item.source ? formatAmount(item.source.originalAmount || item.source.amount) : '',
        item.target?.Name ?? '',
        item.target?.Sku ?? '',
        incomeRef,
        expenseRef
      ];
    };
    const headerRow = headers.map(escapeCsvCell).join(',');
    const dataRows = filteredData.map((item) => getRowValues(item).map(escapeCsvCell).join(','));
    const csv = '\uFEFF' + [headerRow, ...dataRows].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `datapoint-budget-item-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredData.length} rows to CSV`);
  };

  const renderEnhancedFilters = () => (
    <Collapse in={showFilters}>
      <Card sx={{ mb: 2, backgroundColor: '#f8f9fa' }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search budgets or items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <Close />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Items</MenuItem>
                <MenuItem value="matched">Synced Only</MenuItem>
                <MenuItem value="source-only">Not Synced</MenuItem>
                <MenuItem value="target-only">Target Only</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Rows per page</InputLabel>
              <Select
                value={rowsPerPage}
                label="Rows per page"
                onChange={(e) => setRowsPerPage(e.target.value)}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>
    </Collapse>
  );

  const renderBudgetToItemComparison = () => {
    const totalCount = filteredData.length;
    const matchedCount = filteredData.filter(i => i.status === 'matched').length;
    const sourceOnlyCount = filteredData.filter(i => i.status === 'source-only').length;
    const targetOnlyCount = filteredData.filter(i => i.status === 'target-only').length;

    return (
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary 
          expandIcon={<ExpandMore />}
          sx={{ 
            backgroundColor: '#f8f9fa',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <SyncAlt sx={{ color: '#1976d2' }} />
            <Typography variant="h6">
              Autodesk Budget ↔ QuickBooks Item Sync ({totalCount} items)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {/* Enhanced Filters */}
          {renderEnhancedFilters()}
          
          {/* Simplified Table Controls */}
          <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedData.length} of {filteredData.length} items
                {searchTerm && ` • Filtered by "${searchTerm}"`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleRefresh} title="Refresh data">
                  <Refresh fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                  title="Toggle filters"
                >
                  <FilterList fontSize="small" />
                </IconButton>
                <Tooltip title="Download all as CSV">
                  <IconButton size="small" onClick={handleExportCsv} color="primary">
                    <FileDownload fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 80 }}>Sync Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Budget Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Budget Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Budget Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 50 }} align="center">
                    <SyncAlt />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Item SKU</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Income Account Ref</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Expense Account Ref</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm || statusFilter !== 'all' 
                          ? 'No items match your filters' 
                          : 'No data available'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item, index) => (
                    <TableRow 
                      key={index} 
                      sx={{
                        ...getRowStyle(item.status),
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                    >
                      <TableCell>
                        <Tooltip title={getStatusLabel(item.status)}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getStatusIcon(item.status)}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      {/* Source: Budget */}
                      <TableCell>
                        {item.source?.code ? (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.source.code}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.source?.name ? (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.source.name}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.source ? (
                          <Typography variant="body2">
                            {formatAmount(item.source.originalAmount || item.source.amount)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <SyncAlt sx={{ color: item.status === 'matched' ? '#4caf50' : '#ccc' }} />
                      </TableCell>
                      {/* Target: Item */}
                      <TableCell>
                        {item.target?.Name ? (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.target.Name}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Not synced
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.target?.Sku ? (
                          <Typography variant="body2">
                            {item.target.Sku}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.target?.IncomeAccountRef ? (
                          <Typography variant="body2">
                            {typeof item.target.IncomeAccountRef === 'string' 
                              ? item.target.IncomeAccountRef 
                              : (item.target.IncomeAccountRef.value || item.target.IncomeAccountRef.name || item.target.IncomeAccountRef.FullName || '-')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.target?.ExpenseAccountRef ? (
                          <Typography variant="body2">
                            {typeof item.target.ExpenseAccountRef === 'string' 
                              ? item.target.ExpenseAccountRef 
                              : (item.target.ExpenseAccountRef.value || item.target.ExpenseAccountRef.name || item.target.ExpenseAccountRef.FullName || '-')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {totalPages}
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<ArrowBack />} 
          onClick={handleBack}
        >
          Back to Sync Flow
        </Button>
      </Box>
    );
  }

  // Calculate summary data
  const budgets = budgetData?.results || [];
  const items = itemData?.Item || [];
  const totalCount = mergedData.length;
  const syncedCount = mergedData.filter(i => i.status === 'matched').length;
  const notSyncedCount = mergedData.filter(i => i.status === 'source-only').length;
  const targetOnlyCount = mergedData.filter(i => i.status === 'target-only').length;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={handleBack}
        >
          Back to Sync Flow
        </Button>
        <Tooltip title="Configure budget → QB item mapping">
          <IconButton
            color="primary"
            onClick={() => navigate(`/datapoint/${flowInstanceId}/mapping`, {
              state: { masterFlowId: config?.masterFlowId, flowName, tenantId },
            })}
          >
            <Link />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main Comparison Table - no extra header/title/chips; matches Customer/Vendor sync (accordion only) */}
      {renderBudgetToItemComparison()}
    </Box>
  );
};

export default BudgetToItemDatapoint;
